import { useState, useEffect } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://hello-reviews-api.b-9f2.workers.dev';

interface PlatformData {
  name: string;
  category: string;
  url: string;
  icon: string;
  trust: string;
  audience: string;
  verified: boolean;
  apiAccess: boolean | string;
  pricing: string;
  description: string;
}

interface PlatformResearch {
  platform_name: string;
  researched: boolean;
  researched_at?: string;
  notes?: string;
  insights?: string;
  use_cases?: string;
  pros?: string;
  cons?: string;
}

interface AIComparison {
  id?: number;
  question: string;
  platforms?: string;
  ai_response: string;
  created_at?: string;
}

export default function ResearchDashboard() {
  const [researchData, setResearchData] = useState<Record<string, PlatformResearch>>({});
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterResearched, setFilterResearched] = useState<string>('all');
  const [showAICompare, setShowAICompare] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiHistory, setAiHistory] = useState<AIComparison[]>([]);
  const [loadingAI, setLoadingAI] = useState(false);

  // Hardcoded platform data
  const platforms: PlatformData[] = [
    {
      name: 'Angi',
      category: 'industry_specific',
      url: 'https://www.angi.com/',
      icon: '🏠',
      trust: 'Trusted for home services',
      audience: 'Homeowners looking for contractors',
      verified: true,
      apiAccess: false,
      pricing: 'paid',
      description: 'Formerly Angies List. Go-to for home service providers (contractors, plumbers, electricians, HVAC).'
    },
    {
      name: 'Avvo',
      category: 'industry_specific',
      url: 'https://www.avvo.com/',
      icon: '⚖️',
      trust: 'Legal services reviews',
      audience: 'Clients researching lawyers',
      verified: true,
      apiAccess: false,
      pricing: 'freemium',
      description: 'Lawyer ratings and reviews. Peer reviews + client reviews. Legal-specific platform.'
    },
    {
      name: 'Facebook Reviews',
      category: 'general',
      url: 'https://www.facebook.com/business',
      icon: '👥',
      trust: 'Large social reach but lower trust (6% vs Google 66%)',
      audience: 'Billions on Facebook platform',
      verified: false,
      apiAccess: 'API',
      pricing: 'free',
      description: 'Good for social proof and sharing. Less trusted than Google for reviews. Easy integration with social marketing.'
    },
    {
      name: 'G2',
      category: 'industry_specific',
      url: 'https://www.g2.com/',
      icon: '💼',
      trust: '90M annual users, 500K reviews',
      audience: '90 million annual users',
      verified: true,
      apiAccess: 'API',
      pricing: 'freemium',
      description: 'B2B software reviews. Critical for SaaS companies. Examples: Zoom (55K reviews), Slack (32K reviews).'
    },
    {
      name: 'Google Reviews',
      category: 'general',
      url: 'https://www.google.com/business/',
      icon: '🔍',
      trust: '73% market share - dominant player',
      audience: '81% of consumers check before visiting',
      verified: true,
      apiAccess: 'API',
      pricing: 'free',
      description: 'Critical for SEO and local search. Integrates with Google Maps and Search. Most important platform for local businesses.'
    },
    {
      name: 'Groupon',
      category: 'industry_specific',
      url: 'https://www.groupon.com/',
      icon: '💸',
      trust: 'Deal-based platform that collects verified reviews from purchasers',
      audience: 'Popular for local services, especially beauty/wellness',
      verified: true,
      apiAccess: false,
      pricing: 'paid',
      description: 'Businesses running deals get reviews automatically. Problem: Reviews trapped on Groupon, not visible on Google. When deal expires, reviews stop coming in. Common for spas, restaurants, activities.'
    },
    {
      name: 'Healthgrades',
      category: 'industry_specific',
      url: 'https://www.healthgrades.com/',
      icon: '🏥',
      trust: '66% trust Google vs 9% Yelp for healthcare',
      audience: 'Patients researching doctors',
      verified: true,
      apiAccess: false,
      pricing: 'freemium',
      description: 'Healthcare-specific reviews. Doctor ratings and patient reviews. Critical for medical practices.'
    },
    {
      name: 'HomeAdvisor',
      category: 'industry_specific',
      url: 'https://www.homeadvisor.com/',
      icon: '🔨',
      trust: 'Connects homeowners with local professionals',
      audience: 'Popular for home improvement projects',
      verified: true,
      apiAccess: false,
      pricing: 'paid',
      description: 'Lead generation + review platform for home services. Contractors pay for leads.'
    },
    {
      name: 'Yelp',
      category: 'general',
      url: 'https://www.yelp.com/',
      icon: '⭐',
      trust: 'Strong for restaurants, mixed for other categories',
      audience: 'Popular for dining, local services',
      verified: true,
      apiAccess: 'API',
      pricing: 'freemium',
      description: 'Review filtering system can hide legitimate reviews. Strong for restaurants and local businesses. Mobile-first platform.'
    }
  ];

  // Load research data
  useEffect(() => {
    fetchResearchData();
    fetchAIHistory();
  }, []);

  const fetchResearchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platforms/research`);
      const data = await response.json();
      if (data.success && data.data) {
        const researchMap: Record<string, PlatformResearch> = {};
        data.data.forEach((item: any) => {
          researchMap[item.platform_name] = item;
        });
        setResearchData(researchMap);
      }
    } catch (err) {
      console.error('Failed to fetch research data:', err);
    }
  };

  const fetchAIHistory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/platforms/compare/history`);
      const data = await response.json();
      if (data.success && data.data) {
        setAiHistory(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch AI history:', err);
    }
  };

  const toggleResearched = async (platformName: string) => {
    const current = researchData[platformName]?.researched || false;
    const newState = !current;

    // Optimistic update
    setResearchData({
      ...researchData,
      [platformName]: {
        ...researchData[platformName],
        platform_name: platformName,
        researched: newState
      }
    });

    try {
      await fetch(`${API_URL}/api/platforms/research/${platformName}/researched`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ researched: newState })
      });
    } catch (err) {
      console.error('Failed to toggle researched:', err);
      // Revert on error
      setResearchData({
        ...researchData,
        [platformName]: {
          ...researchData[platformName],
          researched: current
        }
      });
    }
  };

  const saveResearch = async (platformName: string, updates: Partial<PlatformResearch>) => {
    try {
      await fetch(`${API_URL}/api/platforms/research/${platformName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...updates,
          researched: researchData[platformName]?.researched || false
        })
      });

      setResearchData({
        ...researchData,
        [platformName]: {
          ...researchData[platformName],
          platform_name: platformName,
          ...updates
        }
      });
    } catch (err) {
      console.error('Failed to save research:', err);
      alert('Failed to save research. Please try again.');
    }
  };

  const askAI = async () => {
    if (!aiQuestion.trim()) return;

    setLoadingAI(true);
    try {
      const response = await fetch(`${API_URL}/api/platforms/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: aiQuestion,
          platforms: []
        })
      });

      const data = await response.json();
      if (data.success && data.data) {
        setAiHistory([data.data, ...aiHistory]);
        setAiQuestion('');
      }
    } catch (err) {
      console.error('Failed to ask AI:', err);
      alert('Failed to get AI response. Please try again.');
    } finally {
      setLoadingAI(false);
    }
  };

  // Filter platforms
  const filteredPlatforms = platforms.filter(p => {
    if (filterCategory !== 'all' && p.category !== filterCategory) return false;
    if (filterResearched === 'researched' && !researchData[p.name]?.researched) return false;
    if (filterResearched === 'not-researched' && researchData[p.name]?.researched) return false;
    return true;
  });

  // Calculate stats
  const researchedCount = Object.values(researchData).filter(r => r.researched).length;
  const totalPlatforms = platforms.length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Research Dashboard</h1>
          <p className="text-xl text-gray-600 mb-4">Interactive platform research workspace</p>

          {/* Progress Bar */}
          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Platforms Researched</span>
              <span className="font-bold">{researchedCount}/{totalPlatforms}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-gradient-to-r from-blue-600 to-green-600 h-3 rounded-full transition-all duration-500"
                style={{ width: `${(researchedCount / totalPlatforms) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded-lg shadow">
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Category:</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              <option value="general">General</option>
              <option value="industry_specific">Industry Specific</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mr-2">Status:</label>
            <select
              value={filterResearched}
              onChange={(e) => setFilterResearched(e.target.value)}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Platforms</option>
              <option value="researched">Researched Only</option>
              <option value="not-researched">Not Researched</option>
            </select>
          </div>
          <button
            onClick={() => setShowAICompare(!showAICompare)}
            className="ml-auto px-4 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition"
          >
            🤖 AI Compare ({aiHistory.length})
          </button>
        </div>

        {/* AI Compare Section */}
        {showAICompare && (
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl shadow-lg p-6 mb-6 border-2 border-purple-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">🤖 Ask AI About Platforms</h3>

            <div className="bg-white rounded-lg p-4 mb-4 border-2 border-purple-200">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={aiQuestion}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && askAI()}
                  placeholder="e.g., Which platform should I recommend for a dentist?"
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={loadingAI}
                />
                <button
                  onClick={askAI}
                  disabled={!aiQuestion.trim() || loadingAI}
                  className="px-8 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingAI ? '⏳' : 'Ask'}
                </button>
              </div>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {aiHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No comparisons yet. Ask a question!</p>
              ) : (
                aiHistory.map((item) => (
                  <div key={item.id} className="bg-white border-2 border-purple-100 rounded-lg p-4">
                    <div className="font-bold text-gray-900 mb-2">💬 {item.question}</div>
                    <div className="text-gray-700 whitespace-pre-wrap">🤖 {item.ai_response}</div>
                    {item.created_at && (
                      <div className="text-xs text-gray-500 mt-2">
                        {new Date(item.created_at).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Platforms Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {filteredPlatforms.map((platform) => {
            const research = researchData[platform.name];
            const isResearched = research?.researched || false;

            return (
              <div
                key={platform.name}
                onClick={() => setSelectedPlatform(platform.name)}
                className={`bg-white rounded-lg shadow-md p-5 cursor-pointer transition-all hover:shadow-xl border-2 ${
                  isResearched ? 'border-green-300 bg-green-50' : 'border-gray-200'
                } ${selectedPlatform === platform.name ? 'ring-4 ring-blue-300' : ''}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">{platform.icon}</span>
                    <h3 className="text-lg font-bold text-gray-900">{platform.name}</h3>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleResearched(platform.name);
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isResearched
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {isResearched ? '✓ Researched' : 'Mark Researched'}
                  </button>
                </div>

                <span className={`inline-block px-2 py-1 rounded text-xs font-medium mb-3 ${
                  platform.category === 'general' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                }`}>
                  {platform.category === 'general' ? 'general' : 'industry_specific'}
                </span>

                <a
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-blue-600 hover:underline text-sm block mb-3"
                >
                  Visit Platform →
                </a>

                <div className="text-sm space-y-2 mb-3">
                  <div>📊 {platform.trust}</div>
                  <div>👥 {platform.audience}</div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  {platform.verified && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">✓ Verified</span>
                  )}
                  {platform.apiAccess && (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                      {typeof platform.apiAccess === 'string' ? platform.apiAccess : 'API'}
                    </span>
                  )}
                  <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs">{platform.pricing}</span>
                </div>

                <p className="text-sm text-gray-600 border-t pt-3">{platform.description}</p>

                {research?.insights && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className="text-xs font-medium text-green-800 mb-1">💡 Your Insights:</div>
                    <div className="text-sm text-gray-700">{research.insights}</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Detail Panel */}
        {selectedPlatform && (
          <DetailPanel
            platform={platforms.find(p => p.name === selectedPlatform)!}
            research={researchData[selectedPlatform]}
            onSave={(updates) => saveResearch(selectedPlatform, updates)}
            onClose={() => setSelectedPlatform(null)}
          />
        )}

        {/* Back Link */}
        <div className="text-center mt-8">
          <a href="#home" className="text-blue-600 hover:underline font-medium">
            ← Back to Home
          </a>
        </div>
      </div>
    </div>
  );
}

// Detail Panel Component
function DetailPanel({
  platform,
  research,
  onSave,
  onClose
}: {
  platform: PlatformData;
  research?: PlatformResearch;
  onSave: (updates: Partial<PlatformResearch>) => void;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState(research?.notes || '');
  const [insights, setInsights] = useState(research?.insights || '');
  const [useCases, setUseCases] = useState(research?.use_cases || '');
  const [pros, setPros] = useState(research?.pros || '');
  const [cons, setCons] = useState(research?.cons || '');

  const handleSave = () => {
    onSave({ notes, insights, use_cases: useCases, pros, cons });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{platform.icon}</span>
            <h2 className="text-3xl font-bold text-gray-900">{platform.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Platform Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-2">Platform Info</h3>
            <div className="text-sm space-y-1">
              <div>📊 {platform.trust}</div>
              <div>👥 {platform.audience}</div>
              <div>💵 {platform.pricing}</div>
              <div>{platform.description}</div>
            </div>
          </div>

          {/* Research Fields */}
          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">
              💡 Key Insights
            </label>
            <textarea
              value={insights}
              onChange={(e) => setInsights(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="What did you learn about this platform? Key discoveries..."
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">
              🎯 When to Use This Platform
            </label>
            <textarea
              value={useCases}
              onChange={(e) => setUseCases(e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Which customers should use this? When is it the right choice?"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                ✅ Pros / Advantages
              </label>
              <textarea
                value={pros}
                onChange={(e) => setPros(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What are the advantages?"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">
                ❌ Cons / Disadvantages
              </label>
              <textarea
                value={cons}
                onChange={(e) => setCons(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="What are the disadvantages?"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-900 mb-2">
              📝 Additional Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any other observations, quotes, or findings..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              💾 Save Research
            </button>
            <button
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
